/* eslint-disable react/jsx-props-no-spreading */
import * as React from 'react';
import { styled } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import Typography from '@mui/material/Typography';

const Accordion = styled(MuiAccordion)({
  '&.Mui-expanded': {
    margin: 0,
  },
});

const AccordionSummary = styled(MuiAccordionSummary)(({ theme }) => ({
  marginTop: theme.spacing(1),
  maxHeight: 48,
  '&.Mui-expanded': {
    minHeight: 48,
  },
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(2),
  flexDirection: 'column',
  borderTop: '1px solid rgba(0, 0, 0, .125)',
}));

interface Props extends AccordionProps {
  label?: string;
  sectionKey: string;
}

/**
 * The CustomAccordion extends the MUIAccordion and requires a sectionKey. The open
 * and closed states are managed internally, but if you need to react to a state
 * change the onChange method is available.
 *
 * More information available in the [Accordion docs](https://mui.com/api/accordion/)
 */
export default function CustomAccordion({ ...props }: Props) {
  const [isOpen, setIsOpen] = React.useState(
    props.defaultExpanded ?? props.sectionKey,
  );
  const sectionKeyRef = React.useRef(props.sectionKey);

  const handleChange = (key: string) => (
    event: React.SyntheticEvent<Element, Event>,
    expanded: boolean,
  ) => {
    setIsOpen(expanded ? key : '');

    if (props.onChange) {
      props.onChange(event, expanded);
    }
  };

  return (
    <Accordion
      expanded={isOpen === sectionKeyRef.current}
      onChange={handleChange(sectionKeyRef.current)}
      sx={{
        '& .MuiCollapse-wrapper': {
          marginBottom: '0px',
        },
      }}
    >
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        data-testid="accordion-header"
        sx={{ marginTop: 0 }}
      >
        <Typography>{props.label}</Typography>
      </AccordionSummary>
      <AccordionDetails data-testid="accordion-content" sx={{ padding: '0px 16px!important' }}>
        {props.children}
      </AccordionDetails>
    </Accordion>
  );
}
